import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import type { OrderRecord } from '@adiox/api';
import { useApi } from '@adiox/ui';
import type { Position } from '../stream/accountModel';
import { Modal } from '@adiox/ui';
import {
  KIND_LABELS,
  normalizeVolume,
  sideOfOrderType,
  type OrderType,
  type Side,
} from '../trading/tradeRules';
import { pointsBetween, referencePrice, type ChartLine } from './chartTrading';

const WHAT: Record<ChartLine['kind'], string> = {
  position: 'Position',
  positionSl: 'Stop loss',
  positionTp: 'Take profit',
  order: 'Order price',
  orderSl: 'Stop loss',
  orderTp: 'Take profit',
};

/**
 * Shown while a line is dragged: the level, its distance in points from the entry, and the profit or loss it would mean.
 * The money figure comes from the server (profit preview), so it uses the same contract and currency rules as real trades.
 */
export function DragReadout({
  drag,
  positions,
  orders,
  symbol,
  digits,
  login,
  balance,
  currency,
}: {
  drag: { line: ChartLine; price: number };
  positions: Position[];
  orders: OrderRecord[];
  symbol: string;
  digits: number;
  login: number | undefined;
  balance: number;
  currency: string;
}) {
  const api = useApi();
  const { line, price } = drag;
  const reference = referencePrice(line, positions, orders);
  const volume =
    positions.find((p) => p.ticket === line.ticket)?.volume ??
    orders.find((o) => o.ticket === line.ticket)?.volumeCurrent;
  const money = line.kind !== 'order' && reference !== null && volume !== undefined;

  // Ask the server only once the pointer has rested for a moment, not on every pixel.
  const [settled, setSettled] = useState(price);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(price), 120);
    return () => clearTimeout(timer);
  }, [price]);

  const preview = useQuery({
    queryKey: ['profit-preview', login, symbol, line.side, volume, reference, settled],
    enabled: money && login !== undefined,
    staleTime: 60_000,
    placeholderData: (previous) => previous,
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/accounts/{login}/profit-preview', {
        params: {
          path: { login: login! },
          query: { symbol, side: line.side, volume: volume!, open: reference!, close: settled },
        },
      });
      return data?.profit ?? null;
    },
  });

  const points = reference === null ? null : pointsBetween(reference, price, digits);
  const profit = preview.data ?? null;
  const share = profit !== null && balance > 0 ? (Math.abs(profit) / balance) * 100 : null;
  const label = line.kind === 'position' ? 'Stop / target' : WHAT[line.kind];

  return (
    <div
      className="num pointer-events-none absolute right-16 top-2 z-20 rounded-md border border-accent bg-panel2/95 px-3 py-1.5 text-xs shadow-lg"
      role="status"
      data-testid="drag-readout"
    >
      <span className="font-semibold">{label}</span> {price.toFixed(digits)}
      {points !== null && (
        <span className="ml-2 text-muted">
          {points > 0 ? '+' : ''}
          {points} pts
        </span>
      )}
      {money && (
        <span
          className={`ml-2 ${profit !== null && profit < 0 ? 'text-down' : profit !== null && profit > 0 ? 'text-up' : 'text-muted'}`}
        >
          {profit === null
            ? '…'
            : `${profit > 0 ? '+' : ''}${profit.toFixed(2)} ${currency}${share !== null ? ` (${share.toFixed(2)}% of balance)` : ''}`}
        </span>
      )}
    </div>
  );
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
  /** Makes the item a checkbox item (shown with a tick when true). */
  checked?: boolean;
  /** Needed when two items can have the same label. */
  key?: string;
  /** A keyboard shortcut shown at the right, for information. */
  shortcut?: string;
}

/**
 * A small keyboard-operable context menu at the pointer: arrows move, Enter chooses, Escape or a click elsewhere closes.
 * With `searchable`, a field at the top filters the items as the user types (Enter takes the first match).
 */
export function ContextMenu({
  x,
  y,
  title,
  items,
  onClose,
  searchable = false,
}: {
  x: number;
  y: number;
  title: string;
  items: MenuItem[];
  onClose: () => void;
  searchable?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const shown = q ? items.filter((i) => i.label.toLowerCase().includes(q)) : items;
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>(searchable ? 'input' : 'button:not(:disabled)')?.focus();
    const away = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener('pointerdown', away, true);
    window.addEventListener('blur', onClose);
    return () => {
      window.removeEventListener('pointerdown', away, true);
      window.removeEventListener('blur', onClose);
    };
  }, [onClose, searchable]);

  const onKey = (e: React.KeyboardEvent) => {
    const buttons = [...(ref.current?.querySelectorAll<HTMLElement>('button:not(:disabled)') ?? [])];
    const at = buttons.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      buttons[0]?.click();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      buttons[(at + 1) % buttons.length]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      buttons[(at - 1 + buttons.length) % buttons.length]?.focus();
    }
  };

  // In a portal: dock panels clip and stack their content, and the menu must sit above every panel and splitter.
  return createPortal(
    <div
      ref={ref}
      role="menu"
      aria-label={title}
      onKeyDown={onKey}
      style={{
        left: Math.min(x, window.innerWidth - 240),
        top: Math.max(8, Math.min(y, window.innerHeight - 40 - (items.length + (searchable ? 1 : 0)) * 32)),
      }}
      className="fixed z-[1000] max-h-[calc(100vh-1rem)] min-w-52 overflow-auto rounded-md border border-line bg-panel py-1 shadow-2xl"
    >
      <div className="px-3 py-1 text-xs uppercase tracking-wide text-muted">{title}</div>
      {searchable && (
        <div className="px-2 pb-1">
          <input
            type="search"
            aria-label={`Search ${title.toLowerCase()}`}
            placeholder="Search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent"
          />
        </div>
      )}
      {searchable && shown.length === 0 && <p className="px-3 py-1.5 text-sm text-muted">Nothing matches.</p>}
      {shown.map((item) => (
        <button
          key={item.key ?? item.label}
          type="button"
          role={item.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
          aria-checked={item.checked}
          disabled={item.disabled}
          onClick={() => {
            onClose();
            item.onSelect();
          }}
          className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-panel2 focus-visible:bg-panel2 focus-visible:outline-none disabled:opacity-40 ${item.danger ? 'text-down' : ''}`}
        >
          {item.checked !== undefined && (
            <span aria-hidden="true" className="inline-block w-5">
              {item.checked ? '✓' : ''}
            </span>
          )}
          {item.label}
          {item.shortcut && <span className="float-right pl-4 text-xs text-muted">{item.shortcut}</span>}
        </button>
      ))}
    </div>,
    document.body,
  );
}

/** Confirms a pending order picked from the chart menu, with the volume to trade, before anything is sent. */
export function PendingConfirm({
  request,
  symbol,
  digits,
  info,
  defaultVolume,
  busy,
  onPlace,
  onClose,
}: {
  request: { type: OrderType; price: number } | null;
  symbol: string;
  digits: number;
  info: { volumeMin: number; volumeMax: number; volumeStep: number } | undefined;
  defaultVolume: number;
  busy: boolean;
  onPlace: (type: OrderType, price: number, volume: number) => Promise<boolean>;
  onClose: () => void;
}) {
  return (
    <Modal open={request !== null} title="Place pending order" onClose={onClose}>
      {request && info && (
        <PendingForm
          key={`${request.type}${request.price}`}
          request={request}
          symbol={symbol}
          digits={digits}
          info={info}
          defaultVolume={defaultVolume}
          busy={busy}
          onPlace={onPlace}
          onClose={onClose}
        />
      )}
    </Modal>
  );
}

function PendingForm({
  request,
  symbol,
  digits,
  info,
  defaultVolume,
  busy,
  onPlace,
  onClose,
}: {
  request: { type: OrderType; price: number };
  symbol: string;
  digits: number;
  info: { volumeMin: number; volumeMax: number; volumeStep: number };
  defaultVolume: number;
  busy: boolean;
  onPlace: (type: OrderType, price: number, volume: number) => Promise<boolean>;
  onClose: () => void;
}): ReactNode {
  const [volume, setVolume] = useState(String(defaultVolume));
  const side: Side = sideOfOrderType(request.type);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        void onPlace(request.type, request.price, normalizeVolume(Number(volume), info)).then(
          (ok) => ok && onClose(),
        );
      }}
    >
      <p className="text-sm">
        <span className={side === 'Buy' ? 'text-up' : 'text-down'}>
          {KIND_LABELS[request.type as keyof typeof KIND_LABELS]}
        </span>{' '}
        on <strong>{symbol}</strong> at{' '}
        <span className="num font-semibold">{request.price.toFixed(digits)}</span>
      </p>
      <div>
        <label className="mb-1 block text-xs uppercase tracking-wide text-muted" htmlFor="pending-volume">
          Volume (lots)
        </label>
        <input
          id="pending-volume"
          autoFocus
          inputMode="decimal"
          value={volume}
          onChange={(e) => setVolume(e.target.value)}
          className="num w-full rounded-md border border-line bg-panel2 px-2 py-1 text-sm outline-none focus:border-accent"
        />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-line px-3 py-1 text-sm hover:border-accent"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy}
          className="rounded-md bg-accent px-3 py-1 text-sm font-semibold text-accent-fg disabled:opacity-40"
        >
          Place order
        </button>
      </div>
    </form>
  );
}
